import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { Check, Loader2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { MarkdownEditor } from '@/components/shared/inputComponents/MarkdownEditor'

/**
 * Inline description editing for the full-size page-editor preview.
 *
 * The full-size `DraggableContentBlockItem` provides this context so the two
 * shared render helpers (`MarkdownPreviewText` / `TextContent`) can display and
 * edit the CURRENT language's description without threading props through every
 * layout.
 *
 * When NO provider is present (the thumbnail path used by ContentBlocksManager
 * and CategoryForm) the helpers fall back to their original English-only
 * rendering — byte-for-byte unchanged.
 */
/**
 * Shared-edit orchestration for the multi-column text styles (2-/3-column).
 *
 * These styles render TWO independent section editors but must open, edit, save,
 * and cancel as ONE unit under a single shared action row (instead of each
 * section owning its own Save/Cancel). The two live section drafts are lifted to
 * the block level so a single Save can join them into one description and run a
 * single mutation. Single-section styles ignore this entirely and keep their own
 * self-contained inline editor.
 */
export interface MultiSectionEditController {
  /** Whether all section editors are currently open. */
  editing: boolean
  /** Whether the shared save is in flight. */
  saving: boolean
  /** Live per-section drafts (index-aligned to `sectionIndex`). */
  drafts: string[]
  /** Open ALL section editors together (snapshots drafts from the stored value). */
  start: () => void
  /** Discard ALL drafts and close ALL section editors together. */
  cancel: () => void
  /** Join the live drafts into one description and persist in ONE mutation. */
  save: () => Promise<void>
  /** Update a single section's live draft. */
  setDraft: (index: number, value: string) => void
}

export interface EditableDescriptionContextValue {
  /** Raw markdown source of the current language's FULL description ('' if none).
   *  For multi-section styles this still contains the in-string section delimiter;
   *  consumers slice out their own section (see `sectionMarker`). */
  rawValue: string
  /** Whether click-to-edit is enabled (full-size AND not open in the editor). */
  editable: boolean
  /** Number of independent text sections this block's style renders (1 for most,
   *  2 for the multi-column text styles). */
  sectionCount: number
  /** Persist a new value for a single section; resolves on success, rejects on
   *  failure. For single-section styles `index` is always 0. */
  onSaveSection: (index: number, next: string) => Promise<void>
  /** Shared multi-section edit controller. Only USED when `sectionCount > 1`;
   *  single-section styles never touch it. */
  multi: MultiSectionEditController
}

const EditableDescriptionContext =
  createContext<EditableDescriptionContextValue | null>(null)

export function EditableDescriptionProvider({
  value,
  children,
}: {
  value: EditableDescriptionContextValue
  children: ReactNode
}) {
  return (
    <EditableDescriptionContext.Provider value={value}>
      {children}
    </EditableDescriptionContext.Provider>
  )
}

/**
 * Returns the inline-edit context when inside a full-size preview, else `null`.
 * Helpers use `null` to mean "render exactly as before" (thumbnail path).
 */
export function useEditableDescription(): EditableDescriptionContextValue | null {
  return useContext(EditableDescriptionContext)
}

/**
 * Wraps already-rendered description output and swaps it for a TipTap rich
 * markdown editor (with toolbar: headings, bullet/ordered lists, bold, etc.)
 * on click.
 *
 * The editor has a toolbar, so blur-to-save is unusable (clicking a toolbar
 * button blurs the contenteditable). Saving is therefore EXPLICIT: a Save /
 * Cancel action row. Esc cancels (restores the original, no save). Save skips
 * when unchanged, guards against duplicate saves, and keeps the edited text in
 * edit mode if the save fails so the user can retry.
 */
/**
 * Controlled (multi-section) driver. When provided, this editor does NOT own its
 * editing / draft / saving state and renders NO Save/Cancel row — a parent (the
 * multi-column shared editor) drives it so several section editors open, edit,
 * and close as one unit under a single shared action row. Single-section callers
 * omit this and get the original self-contained inline editor, unchanged.
 */
export interface EditableDescriptionControlled {
  /** Whether the editor is open (shared across all sections). */
  editing: boolean
  /** Live draft value for this section. */
  value: string
  /** Whether the shared save is in flight (disables click-to-edit). */
  saving: boolean
  /** Update this section's live draft. */
  onChange: (next: string) => void
  /** Open ALL section editors together (clicking any section starts the group). */
  onStartEditing: () => void
  /** Cancel the whole group (used by Esc). */
  onCancel: () => void
  /** Focus this editor when the group opens — only the first section, to avoid
   *  the sections fighting over focus. */
  autoFocus?: boolean
}

export function EditableDescription({
  rawValue,
  editable,
  onSave,
  placeholder,
  inverse,
  children,
  controlled,
}: {
  rawValue: string
  editable: boolean
  /** Persist handler for the self-contained (single-section) path. Omitted in
   *  controlled (multi-section) mode, where saving goes through the shared row. */
  onSave?: (next: string) => Promise<void>
  /** Shown (muted) when editable and there is no description yet. */
  placeholder?: string
  /** Light text styling for dark overlays (text-on-image). */
  inverse?: boolean
  children: ReactNode
  controlled?: EditableDescriptionControlled
}) {
  const isControlled = controlled != null
  const [editingLocal, setEditing] = useState(false)
  const [draftLocal, setDraft] = useState(rawValue)
  const [savingLocal, setSaving] = useState(false)
  const panelRef = useRef<HTMLDivElement | null>(null)

  // In controlled mode the parent owns edit-open / draft / saving state; the
  // local state is inert. This keeps the single-section path byte-for-byte.
  const editing = isControlled ? controlled!.editing : editingLocal
  const saving = isControlled ? controlled!.saving : savingLocal
  const draft = isControlled ? controlled!.value : draftLocal
  const autoFocus = isControlled ? controlled!.autoFocus ?? false : true

  // The old textarea auto-focused on open. MarkdownEditor (shared, off-limits)
  // doesn't, so focus its ProseMirror node locally once edit mode opens, placing
  // the caret at the end of the content. Deps are edit-open state ONLY — never
  // the draft value — so per-keystroke re-renders don't slam the caret to the
  // end (drafts live at the block level in controlled mode).
  useEffect(() => {
    if (!editing) return
    if (!autoFocus) return
    // Defer so TipTap has mounted its contenteditable.
    const id = window.setTimeout(() => {
      const pm = panelRef.current?.querySelector<HTMLElement>('.ProseMirror')
      if (!pm) return
      pm.focus()
      const sel = window.getSelection()
      if (sel) {
        const range = document.createRange()
        range.selectNodeContents(pm)
        range.collapse(false)
        sel.removeAllRanges()
        sel.addRange(range)
      }
    }, 0)
    return () => window.clearTimeout(id)
  }, [editing, autoFocus])

  if (!editable && !editing) {
    return <>{children}</>
  }

  const startEditing = () => {
    if (saving) return
    if (isControlled) {
      // Controlled: opening one section opens ALL of them together.
      controlled!.onStartEditing()
      return
    }
    setDraft(rawValue)
    setEditing(true)
  }

  const cancel = () => {
    if (isControlled) {
      // Controlled: cancel the whole group (drafts + open state live at the
      // block level).
      controlled!.onCancel()
      return
    }
    setEditing(false)
    setDraft(rawValue)
  }

  const handleChange = (next: string) => {
    if (isControlled) controlled!.onChange(next)
    else setDraft(next)
  }

  const commit = async () => {
    // Uncontrolled (single-section) path only — the controlled/multi path saves
    // via its shared action row, never through this component.
    if (!onSave) return
    const next = draft
    if (next === rawValue) {
      setEditing(false)
      return
    }
    if (saving) return
    setSaving(true)
    try {
      await onSave(next)
      setEditing(false)
    } catch {
      // Keep the edited text and stay in edit mode so the user can retry.
    } finally {
      setSaving(false)
    }
  }

  if (!editing) {
    const hasContent = rawValue.trim().length > 0
    return (
      <div
        role="textbox"
        tabIndex={0}
        onClick={startEditing}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault()
            startEditing()
          }
        }}
        title="Click to edit description"
        className={`group/edit -m-1 cursor-text rounded p-1 transition-colors hover:bg-black/[0.03] hover:outline hover:outline-1 hover:outline-dashed ${
          inverse ? 'hover:outline-white/40' : 'hover:outline-gray-300'
        }`}
      >
        {hasContent ? (
          children
        ) : (
          <span
            className={`text-base italic ${
              inverse ? 'text-white/70' : 'text-gray-400'
            }`}
          >
            {placeholder ?? 'Click to add description'}
          </span>
        )}
      </div>
    )
  }

  // Edit mode: rich markdown editor + explicit Save/Cancel. Esc cancels.
  // The whole panel is rendered on a solid light surface (even for the inverse
  // dark-overlay case) so the editor's toolbar and text stay legible while
  // editing. text-left so list/heading formatting reads naturally.
  return (
    <div
      ref={panelRef}
      className="relative w-full min-w-[260px] text-left"
      // Stop editor keystrokes (and Esc) from reaching the page editor; Esc
      // cancels. Keydown bubbles up from the contenteditable to this wrapper.
      onKeyDown={e => {
        if (e.key === 'Escape') {
          e.preventDefault()
          cancel()
        }
        e.stopPropagation()
      }}
      // Clicks inside the editor must not bubble to any parent click-to-edit
      // region (or re-trigger startEditing on an ancestor).
      onClick={e => e.stopPropagation()}
    >
      <div className="rounded-md bg-white p-2 text-gray-800 shadow-sm ring-1 ring-gray-200">
        <MarkdownEditor value={draft} onChange={handleChange} />
        {/* Controlled (multi-section) mode renders no per-section row — the
            single shared confirm/discard row lives outside, below both
            editors. Icon-only: X discards the edit, ✓ stages it into the
            page's draft (see `onSave` — no server write happens here). */}
        {!isControlled && (
          <div className="mt-2 flex items-center justify-end gap-2">
            {saving && (
              <Loader2 className="mr-auto h-4 w-4 animate-spin text-gray-400" />
            )}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              title="Discard changes"
              aria-label="Discard changes"
              disabled={saving}
              onClick={cancel}
              className="h-7 w-7"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
            <Button
              type="button"
              size="icon"
              title="Confirm changes"
              aria-label="Confirm changes"
              disabled={saving}
              onClick={commit}
              className="h-7 w-7"
            >
              <Check className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
